import React, { useEffect, useState } from 'react';
import { Loader } from 'components/atoms/Loader';
import { useTranslations } from 'hooks/useTranslations';
import { ERouteLinks } from 'models/route';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ELegalEntityTypes, LETypes } from 'constants/LegalEntityTypes';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { useLang } from 'models/langContext';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { getLegalEntity, getOrgLegalEntity } from 'requests/legalEntities';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import {
  ILegalEntityExtendedAddress,
  ILegalEntityLicenceFormValues,
} from 'models/legalEntity';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { LegalEntityLicenceForm } from '../LegalEntityLicenceForm';
import { useLegalEntities } from '../hooks';
import { useCase } from 'pages/CaseManagementPage/CasesPage/hooks';
import { Elabels, getBreadcrumbsBasic } from 'pages/CaseManagementPage/CasesPage/helpers';
import { isPathEqualToRouteName } from 'utils/route';
import { ECaseProcessTypes } from 'constants/CaseProcessTypes';

export interface IObject {
  [key: string]: boolean;
}

export interface IDocument {
  name: string;
  weaponLicenceDocumentType: string;
  comment: string;
  file: File | null;
}
const initialFormValues: ILegalEntityLicenceFormValues = {
  caseUniqueId: '',
  weaponLicenceTemplateUniqueId: '',
  general: {
    name: '',
    code: '',
    startDate: '',
    expiryDate: '',
  },
  conditions: {
    weaponNumberLimit: 0,
    goodReasonUniqueIds: null,
    goodReason: '',
    areaOfValidityUniqueId: '',
    competencyUniqueId: '',
    weaponCategorisationUniqueId: '',
    carry: '',
    additionalConditions: '',
    safeStorageDescription: '',
    hasSafeStorage: null,
    safeStorages: [],
  },
  documents: [],
  additionalInformation: null,
  suitabilityAssessmentPassed: false,
  galleryEntryUniqueId: null,
  customFields: [],
};

const LegalEntityLicencePage = () => {
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const [formValues, setFormValues] = useState<ILegalEntityLicenceFormValues>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { id = '', caseId = '' } = useParams<{ id: string; caseId: string }>();
  const [legalEntity, setLegalEntity] = useState<ILegalEntityData | null>(null);
  const [accessibleGoodReason, setAccessibleGoodReason] = useState<IDictionary[]>([]);
  const [orgLegalEntity, setOrgLegalEntity] = useState<IOrgLegalEntityData | null>(null);
  const [searchParams] = useSearchParams();
  const expand = searchParams.get('expand');
  const type = searchParams.get('type');
  const { getLicence, licence, loadDictionaries, weaponLicenceGoodReason } =
    useConfigurationLicence();
  const { getLegalEntityAddressesData, legalEntityAddress } = useLegalEntities();
  const { getCaseData, caseData } = useCase();
  const [primaryAddress, setPrimaryAddress] = useState<ILegalEntityExtendedAddress>();
  const { pathname }: any = useLocation();
  const isFromLE = isPathEqualToRouteName(pathname, ERouteLinks.LegalEntityCaseAddLicencePage);

  useEffect(() => {
    (async function getDataInit() {
      setIsLoading(true);
      if (caseData && caseData.licenceTemplateUniqueId) {
        getLicence(caseData.licenceTemplateUniqueId);
      }
      setIsLoading(false);
    })();
  }, [caseData]);

  useEffect(() => {
    (async function getLegalEntityAddressInit() {
      if (caseData && caseData.legalUniqueId) {
        await getLegalEntityAddressesData(caseData?.legalUniqueId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData]);

  useEffect(() => {
    (async function getCaseInit() {
      if (caseId) {
        await getCaseData(caseId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  useEffect(() => {
    const address = legalEntityAddress.filter((item) => item.isDefault);
    if (address && address.length) {
      setPrimaryAddress(address[0]);
    } else {
      setPrimaryAddress(undefined);
    }
  }, [legalEntityAddress]);

  useEffect(() => {
    (async function init() {
      await loadDictionaries([
        EDictionaryTypes.weaponLicenceCategory,
        EDictionaryTypes.weaponLicenceCompetency,
        EDictionaryTypes.weaponLicenceGoodReason,
        EDictionaryTypes.weaponLicencePaymentStatus,
        EDictionaryTypes.weaponLicencingAuthority,
        EDictionaryTypes.weaponLicenceCondition,
        EDictionaryTypes.Region,
        EDictionaryTypes.Country,
        EDictionaryTypes.OrganisationCategory,
        EDictionaryTypes.OrganisationCivilianType,
        EDictionaryTypes.OrganisationStateType,
        EDictionaryTypes.attachmentDocumentTypeData,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (caseData) {
      if (caseData.legalType === LETypes.individual) {
        (async function getLegalEntityDataInit() {
          setIsLoading(true);
          if (caseData.legalUniqueId) {
            const response = await getLegalEntity(caseData.legalUniqueId);
            if (response) {
              setLegalEntity(response);
            }
            setIsLoading(false);
          }
        })();
        setOrgLegalEntity(null);
      } else {
        (async function getLegalEntityDataInit() {
          setIsLoading(true);
          if (caseData.legalUniqueId) {
            const response = await getOrgLegalEntity(caseData.legalUniqueId);
            if (response) {
              setOrgLegalEntity(response);
            }
            setIsLoading(false);
          }
        })();
        setLegalEntity(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData]);

  useEffect(() => {
    if (!formValues && weaponLicenceGoodReason?.length && licence) {
      if (licence.conditions.goodReasonDictionaryIds.length) {
        const accessibleWeaponLicenceGoodReason = weaponLicenceGoodReason.filter((item) =>
          licence.conditions.goodReasonDictionaryIds.includes(item.uniqueId.toString()),
        );
        setAccessibleGoodReason(accessibleWeaponLicenceGoodReason);
        const goodReasonUniqueIds = accessibleWeaponLicenceGoodReason.reduce(
          (acc, item) => {
            acc[item.uniqueId.toString()] = false;
            return acc;
          },
          {} as Record<string, boolean>,
        );

        setFormValues({
          ...initialFormValues,
          weaponLicenceTemplateUniqueId: licence.uniqueId,
          conditions: {
            ...initialFormValues.conditions,
            areaOfValidityUniqueId: licence.conditions.specificCountryArea
              ? '1'
              : initialFormValues.conditions.areaOfValidityUniqueId,
            goodReasonUniqueIds,
          },
        });
      } else {
        setFormValues({
          ...initialFormValues,
          weaponLicenceTemplateUniqueId: licence.uniqueId,
        });
      }
    }
  }, [weaponLicenceGoodReason, licence]);

  const breadcrumbs = caseData
    ? getBreadcrumbsBasic(
      isFromLE,
      caseData.legalType,
      t,
      caseData.processTypeUniqueId === ECaseProcessTypes.OTHER_REQUEST,
      caseData.legalUniqueId,
    )
    : [];

  return (
    <>
      <Breadcrumbs
        items={[
          ...breadcrumbs,
          {
            label: t(Elabels.caseDetailsHeader),
            route: isFromLE
              ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':caseId', caseId).replace(':id', id)}}?type=${type}&expand=${expand}`
              : `${ERouteLinks.CasesDetailsPage.replace(':caseId', caseId)}`,
          },
          t('legalEntityDetails.addNewlicence.label'),
        ]}
      />

      {licence &&
        formValues &&
        caseData &&
        ((caseData.legalType === LETypes.individual && legalEntity) ||
          (caseData.legalType === LETypes.organization && orgLegalEntity)) &&
        !isLoading ? (
        <LegalEntityLicenceForm
          licence={licence}
          caseData={caseData}
          legalEntity={
            caseData.legalType === LETypes.individual ? legalEntity : orgLegalEntity
          }
          initialFormValues={formValues}
          primaryAddress={primaryAddress}
          accessibleWeaponLicenceGoodReason={accessibleGoodReason}
          availableAddresses={legalEntityAddress}
          isFromLE={isFromLE}
        />
      ) : (
        <Loader isLoading />
      )}
    </>
  );
};

export { LegalEntityLicencePage };
